import { createSlice, createAsyncThunk, createAction, createSelector } from "@reduxjs/toolkit";
import { oauth } from "../utils";
import { toast } from "react-toastify";
import { SplitShip } from "../types";
import { getIdentityUtils } from "../utils";
import { Status } from "../constants";
import { SplitShipError } from "../components/split-ship/SplitShipErrorPopup";
import { createLoadOrder } from '../actions/order';
import { getFullItems } from "../selectors";

interface SplitshipBreakdownState {
  breakdownId: string;
  item: any;
  external_sku: string;
  color: any;
  size: any;
  image: any;
}
export interface SplitShipState {
  data: SplitShip | null;
  loading: "idle" | "pending";
  putLoading: "idle" | "pending";
  error: string | null;
  uploadProgress: number;
  spreadSheetErrors: SplitShipError | null;
  batchErrors: SplitShipError | null;
  readyForCsv: boolean;
  breakdowns: SplitshipBreakdownState[];
  spreadsheetUrl: string;
}

const initialState: SplitShipState = {
  data: null,
  loading: "idle",
  putLoading: "idle",
  error: null,
  uploadProgress: 0,
  readyForCsv: false,
  breakdowns: [],
  spreadSheetErrors: null,
  batchErrors: null,
  spreadsheetUrl: '',
};

// Async thunks
export const createSplitShip = createAsyncThunk(
  "splitShip/create",
  async (orderId: string) => {
    const { json } = await oauth("POST", "split-ship", {
      action: "create",
      order_id: orderId,
    });
    return json.split_ship;
  },
);

export const SPLIT_SHIP_REOPEN = 'splitShip/reopen';
export const reopenSplitShip = createAsyncThunk(
  SPLIT_SHIP_REOPEN,
  async (orderId: string) => {
    const { json } = await oauth("POST", "split-ship", {
      action: "create",
      order_id: orderId,
      reopen: true,
    });
    return json.split_ship;
  },
);

export const SPLIT_SHIP_RESET = 'splitShip/reset';
export const resetSplitShip = createAsyncThunk(
  SPLIT_SHIP_RESET,
  async (orderId: string) => {
    const { json } = await oauth("POST", "split-ship", {
      action: "reset",
      order_id: orderId,
    });
    return json.split_ship;
  },
);

export const SPLIT_SHIP_RETRIEVE = 'splitShip/retrieve';
export const retrieveSplitShip = createAsyncThunk(
  SPLIT_SHIP_RETRIEVE,
  async (orderId: string) => {
    const { json } = await oauth("GET", `split-ship/${orderId}`, {});
    return json?.split_ship;
  },
);

export const SPLIT_SHIP_DELETE = 'splitShip/delete';
export const deleteSplitShip = createAsyncThunk(
  SPLIT_SHIP_DELETE,
  async (orderId: string) => {
    await oauth("DELETE", `split-ship/${orderId}`);
    return orderId;
  },
);

export const SPLIT_SHIP_UPDATE_EXTERNAL_SKU = 'splitShip/updateExternalSku';
export const updateExternalSku = createAsyncThunk(
  SPLIT_SHIP_UPDATE_EXTERNAL_SKU,
  async (
    params: { orderId: string; splitShipId: string; breakdownId: string; value: string },
    { dispatch, rejectWithValue }
  ) => {
    const { orderId, splitShipId, breakdownId, value } = params;
    try {
      const { json } = await oauth("PUT", "split-ship", {
        action: 'updateExternalSku',
        split_ship_id: splitShipId,
        breakdown_id: breakdownId,
        external_sku: value,
      });
      const { payload } = await dispatch(retrieveSplitShip(orderId));
      return {
        splitShip: payload,
        readyForCsv: json?.ready_for_csv,
        orderId,
        breakdowns: { breakdownId, external_sku: value }
      };
    } catch (e: any) {
      const errors = e?.json?.errors;
      let message: string;
      if (Array.isArray(errors) && errors.length > 0) {
        message = String(errors[0]);
      } else if (errors && typeof errors === 'object') {
        message = String(Object.values(errors)[0]);
      } else {
        message = 'Failed to update external SKU';
      }
      return rejectWithValue(message);
    }
  },
);

export const SPLIT_SHIP_TOGGLE_SUMMARIZE_TAXES = 'splitShip/toggleSummarizeTaxes';
export const toggleSummarizeTaxes = createAsyncThunk(
  SPLIT_SHIP_TOGGLE_SUMMARIZE_TAXES,
  async (
    params: { splitShipId: string; },
    { dispatch }
  ) => {
    const { splitShipId } = params;
    const { json: { split_ship }} = await oauth("PUT", "split-ship", {
      action: 'toggleSummarizeTaxes',
      split_ship_id: splitShipId,
    });
    return split_ship;
  },
);

export const SPLIT_SHIP_GENERATE_SAMPLE_SPREADSHEET = 'splitShip/generateSampleSpreadsheet';
export const generateSampleSpreadsheet = createAsyncThunk(
  SPLIT_SHIP_GENERATE_SAMPLE_SPREADSHEET,
  async (
    params: { splitShipId: string }
  ) => {
    const { splitShipId } = params;
    const { json } = await oauth("POST", "split-ship", {
      action: "generate",
      split_ship_id: splitShipId,
    });

    return {
      splitShip: json?.split_ship,
    };
  }
);

export const SPLIT_SHIP_GET_UPLOAD_SPREADSHEET_URL = 'splitShip/getUploadSpreadsheetUrl';
export const getUploadSpreadsheetUrl = createAsyncThunk(
  SPLIT_SHIP_GET_UPLOAD_SPREADSHEET_URL,
  async (
    params: { spreadsheetId: string },
  ) => {
    const { spreadsheetId } = params;
    const { json } = await oauth("GET", `artifact?id=${spreadsheetId}&url=true`, {});
    return { url: json?.url, spreadsheetId };
  }
);

export interface SplitShipErrorParams {
  splitShipId: string;
  cursor?: string;
}
export const SPLIT_SHIP_GET_ERRORS = 'splitShip/getErrors';
export const getErrors = createAsyncThunk(
  SPLIT_SHIP_GET_ERRORS,
  async (
    params: SplitShipErrorParams,
  ) => {
    const reqParams = {
      split_ship_id: params.splitShipId,
    };
    if (params.cursor) {
      Object.assign(reqParams, { cursor: params.cursor });
    }
    const { json } = await oauth("GET", "split-ship/get-error", reqParams);
    return json;
  }
);

export const SPLIT_SHIP_GET_BATCH_ERRORS = 'splitShip/getBatchErrors';
export const getBatchErrors = createAsyncThunk(
  SPLIT_SHIP_GET_BATCH_ERRORS,
  async (
    params: SplitShipErrorParams,
  ) => {
    const reqParams = {
      split_ship_id: params.splitShipId,
    };
    if (params.cursor) {
      Object.assign(reqParams, { cursor: params.cursor });
    }
    const { json } = await oauth("GET", "split-ship/get-batch-error", reqParams);
    return json;
  }
);

export const SPLIT_SHIP_GET_STATUS = 'splitShip/getStatus';
export const getStatus = createAsyncThunk(
  SPLIT_SHIP_GET_STATUS,
  async (params: { splitShipId: string }, { dispatch, getState }) => {
    const { splitShipId } = params;
    const { json } = await oauth("GET", "split-ship/get-status", {
      split_ship_id: splitShipId
    });

    if (json?.status_id === Status.SPLIT_SHIP_COMPLETED) {
      const state = getState() as { splitShip: SplitShipState };
      const parentId = state.splitShip.data?.parent_id;
      if (parentId) {
        dispatch(createLoadOrder(parentId, true));
      }
    }
    return json?.status_id;
  });

export const SPLIT_SHIP_CALCULATE_TAX = 'splitShip/calculateTax';
export const calculateTax = createAsyncThunk(
  SPLIT_SHIP_CALCULATE_TAX,
  async (params: { splitShipId: string }) => {
    const { splitShipId } = params;
    const { json } = await oauth("POST", "split-ship", {
      action: "calculate",
      split_ship_id: splitShipId,
    });
    return json?.split_ship;
  }
);

export const SPLIT_SHIP_UPLOAD_SPREADSHEET = 'splitShip/uploadSpreadsheet';
export const uploadSpreadsheet = createAction(SPLIT_SHIP_UPLOAD_SPREADSHEET);

const slice = createSlice({
  name: "splitShip",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setUploadProgress: (state, action) => {
      state.uploadProgress = action.payload;
    },
    resetState: () => initialState,
    setBreakdowns: (state, action) => {
      state.breakdowns = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Create Split Ship
      .addCase(createSplitShip.pending, (state) => {
        state.loading = "pending";
        state.error = null;
      })
      .addCase(createSplitShip.fulfilled, (state, action) => {
        state.loading = "idle";
        state.data = action.payload;
      })
      .addCase(createSplitShip.rejected, (state, action) => {
        state.loading = "idle";
        state.error = action.error?.message || "Failed to create split ship";
        toast.error(state.error);
      })

      // Reopen Split Ship
      .addCase(reopenSplitShip.pending, (state) => {
        state.loading = "pending";
        state.error = null;
      })
      .addCase(reopenSplitShip.fulfilled, (state, action) => {
        state.loading = "idle";
        state.data = action.payload;
      })
      .addCase(reopenSplitShip.rejected, (state, action) => {
        state.loading = "idle";
        state.error = action.error?.message || "Failed to reenable split ship";
        toast.error(state.error);
      })

      // Reset Split Ship
      .addCase(resetSplitShip.pending, (state) => {
        state.loading = "pending";
        state.error = null;
      })
      .addCase(resetSplitShip.fulfilled, (state, action) => {
        if (state?.data?.status_id && (state.data.status_id !== action.payload?.status_id)) {
          toast.warn('Taxes need to be recalculated due to changes made that impact taxes.');
        }
        state.loading = "idle";
        state.data = action.payload;
      })
      .addCase(resetSplitShip.rejected, (state, action) => {
        state.loading = "idle";
        state.error = action.error?.message || "Failed to reset split ship";
        toast.error(state.error);
      })

      // Retrieve Split Ship
      .addCase(retrieveSplitShip.pending, (state) => {
        state.loading = "pending";
        state.error = null;
      })
      .addCase(retrieveSplitShip.fulfilled, (state, action) => {
        state.loading = "idle";
        state.data = action.payload;
      })
      .addCase(retrieveSplitShip.rejected, (state, action) => {
        // Split ship is disabled, or not created yet. Treat it as disabled.
        state.loading = "idle";
        state.data = null;
        state.readyForCsv = false;
        state.breakdowns = [];
      })

      // Delete Split Ship
      .addCase(deleteSplitShip.pending, (state) => {
        state.loading = "pending";
        state.error = null;
      })
      .addCase(deleteSplitShip.fulfilled, (state) => {
        state.loading = "idle";
        state.data = null;
        state.readyForCsv = false;
        state.breakdowns = [];
      })
      .addCase(deleteSplitShip.rejected, (state, action) => {
        state.loading = "idle";
        state.error = action.error?.message || "Failed to disable split ship";
        toast.error(state.error);
      })

      // Put Toggle Summarize Taxes
      .addCase(toggleSummarizeTaxes.pending, (state) => {
        state.putLoading = "pending";
        state.error = null;
      })
      .addCase(toggleSummarizeTaxes.fulfilled, (state, action) => {
        state.putLoading = "idle";
        state.data = action.payload;
      })
      .addCase(toggleSummarizeTaxes.rejected, (state, action) => {
        state.putLoading = "idle";
        state.error = action.error?.message || "Failed to Toggle Summarize Taxes";
        toast.error(state.error);
      })

      // Put External SKUs
      .addCase(updateExternalSku.pending, (state) => {
        state.putLoading = "pending";
        state.error = null;
      })
      .addCase(updateExternalSku.fulfilled, (state, action) => {
        state.putLoading = "idle";
        state.data = action.payload.splitShip;
        state.readyForCsv = action.payload?.readyForCsv || false;
        state.breakdowns.forEach((breakdown) => {
          const { breakdownId, external_sku } = action.payload.breakdowns;
          if (breakdown.breakdownId === breakdownId) {
            breakdown.external_sku = external_sku;
          }
        });
      })
      .addCase(updateExternalSku.rejected, (state, action) => {
        state.putLoading = "idle";
        state.error = (action.payload as string) || action.error?.message || "Failed to update external SKU";
        toast.error(state.error);
      })

      // Get errors
      .addCase(getErrors.fulfilled, (state, action) => {
        // we passed in a cursor, append data
        if (action.meta.arg.cursor) {
          state.spreadSheetErrors.meta = action.payload.meta;
          state.spreadSheetErrors.split_ship_item_errors = [
            ...(state.spreadSheetErrors.split_ship_item_errors || []),
            ...(action.payload.split_ship_item_errors || []),
          ];
        } else {
          state.spreadSheetErrors = action.payload;
        }
      })
      .addCase(getErrors.pending, (state) => {
        state.error = null;
      })
      .addCase(getErrors.rejected, (state, action) => {
        // only clear spreadSheetErrors if we asked for the first page
        if (!action.meta.arg.cursor) {
          state.spreadSheetErrors = initialState.spreadSheetErrors;
        }
        toast.error("Failed to get split ship errors");
      })
      // Generate Sample Spreadsheet
      .addCase(generateSampleSpreadsheet.pending, (state) => {
        state.putLoading = "pending";
        state.error = null;
      })
      .addCase(generateSampleSpreadsheet.fulfilled, (state, action) => {
        state.putLoading = "idle";
        state.data = action.payload.splitShip;
      })
      .addCase(generateSampleSpreadsheet.rejected, (state, action) => {
        state.putLoading = "idle";
        state.error = action.error?.message || "Failed to generate sample spreadsheet";
        toast.error(state.error);
      })

      // get batch errors
      .addCase(getBatchErrors.fulfilled, (state, action) => {
        // we passed in a cursor, append data
        if (action.meta.arg.cursor) {
          state.batchErrors.meta = action.payload.meta;
          state.batchErrors.split_ship_item_errors = [
            ...(state.batchErrors.split_ship_item_errors || []),
            ...(action.payload.split_ship_item_errors || []),
          ];
        } else {
          state.batchErrors = action.payload;
        }
      })
      .addCase(getBatchErrors.rejected, (state, action) => {
        state.batchErrors = null;
        toast.error("Failed to get split ship batch errors");
      })
      // get Spreadsheet URL
      .addCase(getUploadSpreadsheetUrl.pending, (state) => {
        state.error = null;
      })
      .addCase(getUploadSpreadsheetUrl.fulfilled, (state, action) => {
        state.spreadsheetUrl = action.payload.url || '';
        state.data.spreadsheet_id = action.payload.spreadsheetId;
      })
      .addCase(getUploadSpreadsheetUrl.rejected, (state, action) => {
        state.error = action.error?.message || "Failed to get spreadsheet URL";
        toast.error(state.error);
      })
      .addCase(uploadSpreadsheet.type, (state) => {
        state.spreadSheetErrors = null;
        state.batchErrors = null;
        state.data.status_id = Status.SPLIT_SHIP_PROCESSING_CSV;
      })
      // Get Status
      .addCase(getStatus.pending, (state) => {
        state.error = null;
      })
      .addCase(getStatus.fulfilled, (state, action) => {
        if (state.data) {
          state.data.status_id = action.payload;
        }
      })
      .addCase(getStatus.rejected, (state, action) => {
        state.error = action.error?.message || "Failed to get status";
        toast.error(state.error);
      })
      // Calculate Tax
      .addCase(calculateTax.pending, (state) => {
        state.error = null;
      })
      .addCase(calculateTax.fulfilled, (state, action) => {
        state.data = action.payload;
      })
      .addCase(calculateTax.rejected, (state, action) => {
        state.error = action.error?.message || "Failed to calculate tax";
        toast.error(state.error);
      });
  },
});

// Selectors
export const selectSplitShip = (state: { splitShip: SplitShipState }) =>
  state.splitShip?.data;

export const splitShipIsLoading = (state: { splitShip: SplitShipState }) =>
  (state?.splitShip?.loading !== "idle" && state?.splitShip?.putLoading !== "pending");

export const selectSplitShipAccessible = (state: any) => {
  const { entities, identity } = state;
  if (!entities || !entities?.company_data || !identity) return false;

  const { hasCapabilities } = getIdentityUtils(identity);
  const isXero = entities.company_data.accounting_software === "XERO";
  const hasSplitShip = hasCapabilities([
    "HAS-SPLIT-SHIP",
  ]);

  return !isXero && hasSplitShip;
};

export const selectSplitShipLoading = (state: { splitShip: SplitShipState }) =>
  state.splitShip.loading === "pending";
export const selectSplitShipError = (state: { splitShip: SplitShipState }) =>
  state.splitShip.error;
export const selectUploadProgress = (state: { splitShip: SplitShipState }) =>
  state.splitShip.uploadProgress;

// Status helper selectors
export const selectIsProcessing = (state: { splitShip: SplitShipState }) => {
  const splitShip = state.splitShip.data;
  return (
    splitShip &&
    [
      Status.SPLIT_SHIP_PROCESSING_CSV,
      Status.SPLIT_SHIP_CALCULATING_TAX,
    ].includes(splitShip.status_id)
  );
};

export const selectHasErrors = (state: { splitShip: SplitShipState }) => {
  const splitShip = state.splitShip.data;
  return (
    splitShip &&
    [Status.SPLIT_SHIP_CSV_ERROR, Status.SPLIT_SHIP_TAX_ERROR].includes(
      splitShip.status_id,
    )
  );
};

export const selectCanUploadSpreadsheet = (state: {
  splitShip: SplitShipState;
}) => {
  const splitShip = state.splitShip.data;
  return (
    splitShip &&
    [Status.SPLIT_SHIP_READY_FOR_CSV, Status.SPLIT_SHIP_CSV_ERROR].includes(
      splitShip.status_id,
    )
  );
};

export const selectCanCalculateTax = (state: { splitShip: SplitShipState }) => {
  const splitShip = state.splitShip.data;
  return splitShip && splitShip.status_id === Status.SPLIT_SHIP_READY_FOR_TAX;
};

export const selectIsCompleted = (state: { splitShip: SplitShipState }) => {
  const splitShip = state.splitShip.data;
  return splitShip && splitShip.status_id === Status.SPLIT_SHIP_COMPLETED;
};

export const selectSpreadsheetUrl = (state: { splitShip: SplitShipState }) =>
  state.splitShip.spreadsheetUrl;

export const selectCSVErrors = (state: { splitShip: SplitShipState }) =>
  state.splitShip.spreadSheetErrors;

export const selectBatchErrors = (state: { splitShip: SplitShipState }) =>
  state.splitShip.batchErrors;

export const selectSplitShipBreakdowns = createSelector(
  [getFullItems, (state, orderId) => orderId],
  (fullItems, orderId) => {
    const breakdowns = [];

    const findPSvalue = (skus, breakdown_product_sku_id) => {
      const skuItem = (skus || []).find(({ product_sku_id }) => {
        return breakdown_product_sku_id === product_sku_id;
      });
      let color_size = {color: null, size: null};
      try {
        color_size = JSON.parse(skuItem?.sku_options || '{}' );
      } catch (e) {}

      return {
        color_name: color_size?.color ?? 'TBD',
        size_name: color_size?.size ?? 'TBD',
        color_id: color_size?.color ?? 'TBD',
        size_id: color_size?.size ?? 'TBD',
      };
    };
    const isPsItem = (item) => {
      return ('ps-products' === item?.copied_from && !!item?.ext_fob_id);
    };
    fullItems.forEach(item => {
      if (item.order_id === orderId) {
        Object.values(item.breakdowns).forEach((breakdown: any) => {
          const promoStandardsValues = isPsItem(item) ?
            findPSvalue(item?.skus || [], breakdown.product_sku_id) : null;
          const color = promoStandardsValues ??
            (item.colors || []).find(c => c.color_id === breakdown.color_id);
          const size = promoStandardsValues ??
            (item.sizes || []).find(s => s.size_id === breakdown.size_id);
          breakdowns.push({
            breakdownId: breakdown.breakdown_id,
            item: {
              item_sku: item.item_sku,
              item_name: item.item_name,
              item_images: item.item_images,
              item_description: item.item_description,
              division_name: item.division_name,
            },
            external_sku: breakdown.external_sku || '',
            catalog_item_breakdown_id: breakdown.catalog_item_breakdown_id || null,
            color: color,
            size: size,
            image: item.image || breakdown.image || null,
            breakdown_id: breakdown.breakdown_id,
            breakdown_name: breakdown.breakdown_name,
            breakdown_qty: breakdown.breakdown_qty,
            breakdown_price: breakdown.breakdown_price,
          });
        });
      }
    });
    return breakdowns;
  }
);

export const { clearError, setUploadProgress, resetState } = slice.actions;
export default slice;
